import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import type { Category, Topic, TopicStatusTone } from "@/types/content";

export const SITE_NAME = "瓜田雷达";
export const SITE_URL = "https://lszhyx.cn";
export const SITE_TAGLINE = "网曝吃瓜 · 进展全程追踪";
export const SITE_EMAIL = "hello@guatianradar.com";
export const CORRECTION_EMAIL = "correction@guatianradar.com";

export const allTopics: Topic[] = topics as unknown as Topic[];
export const allCategories: Category[] = categories as unknown as Category[];
export const filterCategories: Category[] = categories.filter((item) => item.id !== "all");

export const statusToneClass: Record<TopicStatusTone, string> = {
  hot: "bg-primary-500 text-background-50 border-primary-600",
  reply: "bg-accent-500 text-foreground-950 border-accent-600",
  clear: "bg-secondary-100 text-secondary-800 border-secondary-300",
  track: "bg-foreground-900 text-background-50 border-foreground-900",
  cool: "bg-background-200 text-foreground-600 border-background-300",
};

export function getTopicById(id: string): Topic | undefined {
  return allTopics.find((item) => item.id === id);
}

export function getCategoryById(id: string): Category | undefined {
  return allCategories.find((item) => item.id === id);
}

export function getCategoryLabel(id: string): string {
  return getCategoryById(id)?.label ?? "话题档案";
}

export function getTopicsByCategory(categoryId: string): Topic[] {
  if (categoryId === "all") {
    return allTopics;
  }
  return allTopics.filter((item) => item.categoryId === categoryId);
}

export function sortByHeat(list: Topic[]): Topic[] {
  return [...list].sort((a, b) => b.heat - a.heat);
}

export function sortByUpdated(list: Topic[]): Topic[] {
  return [...list].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function getRelatedTopics(topic: Topic, count = 3): Topic[] {
  const related = topic.relatedIds
    .map((id) => getTopicById(id))
    .filter((item): item is Topic => Boolean(item));
  if (related.length >= count) {
    return related.slice(0, count);
  }
  const fillers = allTopics.filter(
    (item) => item.id !== topic.id && !related.some((rel) => rel.id === item.id),
  );
  return [...related, ...sortByHeat(fillers)].slice(0, count);
}

export function getLatestTimeline(count = 6) {
  const entries = allTopics.flatMap((topic) =>
    topic.timeline.map((node) => ({
      topicId: topic.id,
      topicTitle: topic.title,
      categoryId: topic.categoryId,
      statusTone: topic.statusTone,
      status: topic.status,
      date: node.date,
      title: node.title,
      detail: node.detail,
    })),
  );
  return entries
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, count);
}

export function formatDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) {
    return iso;
  }
  return `${year}年${Number(month)}月${Number(day)}日`;
}

export function formatShortDate(iso: string): string {
  const [, month, day] = iso.split("-");
  if (!month || !day) {
    return iso;
  }
  return `${Number(month)}月${Number(day)}日`;
}

export function formatHeat(heat: number): string {
  return heat.toLocaleString("en-US");
}

export function heatPercent(heat: number): number {
  const max = 10000;
  return Math.min(100, Math.round((heat / max) * 100));
}